import type { PaintedActor, PaintedRenderer, ActionWindow } from '../../../packages/view-phaser/src/painted-renderer.ts';
import type { CombatEvent, CombatState, WindupAdvice } from '../../../packages/sim/src/combat/index.ts';
import type { StorySession } from '../../../packages/sim/src/campaign/story.ts';
import { toPixel } from './painted-world.ts';
import { PC_ID } from './scene.ts';
import contentJson from '../../../content/generated/arcs/unburied/playable.json' with { type: 'json' };
type Hireling = { templateId: string; person: { presentationKey: string } };
const CONTENT = contentJson as unknown as { scenes: readonly { id: string; personId: string }[]; companions: Record<string, readonly Hireling[]>; locals?: readonly Hireling[] };
// A person's presentationKey is their unique identity in the ledger (a regional local registers under their own id),
// not a sprite: a hireling is drawn as their template's authored sheet.
const HIRELING_SHEETS: Readonly<Record<string, string>> = Object.fromEntries([...Object.values(CONTENT.companions).flat(), ...CONTENT.locals ?? []]
  .map(hireling => [hireling.templateId, hireling.person.presentationKey.replace(/^presentation:/, '')]));
import { availableActions, kitTemplateIdOf } from '../../../packages/sim/src/items/index.ts';

const cast: Readonly<Record<string, string>> = {
  nessa: 'actor-nessa-tal', melian: 'actor-melian-sar', odel: 'actor-odel-thann',
  noll: 'actor-tavren-noll', sela: 'actor-sela-venn', guard: 'actor-guard',
  thief: 'actor-thief', 'gate-custodian': 'actor-guard',
  'kiln-office-guard': 'actor-guard', 'road-guard': 'actor-guard',
  'toll-guard': 'actor-guard', 'nessa-tal': 'actor-nessa-tal', 'melian-sar': 'actor-melian-sar',
  'odel-thann': 'actor-odel-thann', 'tavren-noll': 'actor-tavren-noll', 'sela-venn': 'actor-sela-venn',
  // Caldris: the clerk and the runner have their own figures; the claimant and advocate borrow the nearest slice figures.
  'z01-watch-captain': 'actor-z01-forum-watch', 'z01-register-widow': 'actor-waiting-family', 'z01-senate-clerk': 'actor-z01-clerk',
  'z01-claimant': 'actor-sela-venn', 'z01-testimony-advocate': 'actor-tavren-noll', 'z01-bond-runner': 'actor-z01-bond-runner',
  // Nacre Quay: a quay guard and a stevedore of its own, and Vos's own figure; the Convener borrows a slice figure.
  'z02-sereth-vann': 'actor-z02-dock-worker', 'z02-bonded-ropemaker': 'actor-z02-dock-worker', 'z02-quay-inspector': 'actor-z02-quay-guard',
  'z02-maurel-neth': 'actor-tavren-noll', 'z02-ildren-vos': 'actor-ildren-vos', 'z02-hook-hand': 'actor-z02-dock-worker',
  // The Broken Causeway: its two sheets dress the lease guard and the contractor; the others borrow slice figures.
  'z06-lease-guard': 'actor-z06-toll-guard', 'z06-works-contractor': 'actor-z06-causeway-labourer',
  // The finale's one fight: the advocate's doorman at the burial annex.
  'n03-annex-doorman': 'actor-guard',
  'z06-toll-factor': 'actor-tavren-noll', 'z06-bonded-mason': 'actor-heavy-labourer', 'z06-ford-guide': 'actor-nessa-tal',
};

// Combat lines are shared by every foe, and no person or encounter records a pronoun: they name the foe or
// the blow, never "he" or "she" (performance.test.ts checks every template).
/** The wind-up line's words for each thing the sim advises (windupAnswer). */
export const WINDUP_ADVICE: Record<WindupAdvice, string> = {
  lunge: 'the arm goes back, and the body behind it is open', brace: 'it comes straight on, from the front',
  withdraw: 'heavy, and aimed at where you stand now', trade: 'a light blow, soon over',
  covered: 'straight into your raised shield', dressing: 'the dressing will be tied before it lands',
  finish: 'your own blow lands first, and it is enough', answering: '', late: 'too late to do anything but take it',
  quick: 'a step back will not carry you clear of it',
};
/** What the opening pause of a face-to-face fight adds before "Choose an action". */
export const combatOpening = (bow: boolean): string => bow
  ? 'Your bow already reaches: Attack looses arrows during the approach, and they land weaker at close quarters. '
  : 'It takes a few seconds to close the distance. ';

export function appearanceFor(id: string, story: StorySession, state: CombatState, fallback = 'actor-kiln-worker'): string {
  // The player, and a survivor (the outsider they were), are drawn with the weapon in hand: spear and shield, bow,
  // blade, or the hooded knife-carrier for a belt knife.
  const outsider = story.slice.continuity.persons[id];
  if (id === PC_ID || outsider?.original) {
    const kit = outsider?.current.equipment.flatMap(item => story.slice.continuity.items[item] ? [kitTemplateIdOf(story.slice.continuity.items[item]!)] : []) ?? [];
    return kit.includes('spear-bronze') ? 'actor-outsider-spear-shield-rotation' : kit.includes('bow-ash') ? 'actor-towpath-scout-rotation'
      : kit.includes('knife-belt') && !kit.includes('blade-iron') ? 'actor-thief' : 'actor-outsider-sword-rotation';
  }
  const candidate = story.candidates.find(candidate => candidate.person.id === id);
  if (candidate?.templateId === 'displaced-mason') return 'actor-heavy-labourer';
  if (candidate) return HIRELING_SHEETS[candidate.templateId] ?? candidate.person.presentationKey.replace(/^presentation:/, '');
  // A speaker is drawn as the person their scene names. A named foe keeps their own figure, and so does whoever takes their post in a later life.
  const own = cast[(CONTENT.scenes.find(scene => scene.id === id)?.personId ?? id).replace(/^person:/, '').replace(/-(?:successor|life)-\d+$/, '')];
  if (own) return own;
  const policy = state.tactics?.enemies[id]?.kind;
  if (policy) return policy === 'working-dead' ? 'actor-working-dead' : `actor-${policy}`;
  return fallback;
}

type Started = Extract<CombatEvent, { type: 'action-started' }>;
/** The sim's reason when a spear lunge breaks a committed action. */
export const SPEAR_BREAK = 'The spear interrupted the committed action.';

/** Owns only presentation history. The sim has already committed every result. */
export class CombatPerformance {
  #seen = 0;
  #strikes = new Map<string, { started: Started; resolved?: number }>();
  #activities = new Map<string, 'idle' | 'work'>();

  reset(): void { this.#seen = 0; this.#strikes.clear(); this.#activities.clear(); }

  drain(state: CombatState, renderer: Pick<PaintedRenderer, 'impact'> & Partial<Pick<PaintedRenderer, 'burst' | 'hitStop'>>): readonly CombatEvent[] {
    const events = state.events.slice(this.#seen);
    this.#seen = state.events.length;
    const at = (id: string) => toPixel(state.actors[id]!.position);
    for (const event of events) {
      if (event.type === 'intention') this.#activities.set(event.actorId, event.goal === 'work' ? 'work' : 'idle');
      if (event.type === 'action-started') {
        this.#strikes.delete(event.actorId);
        if (event.action.type === 'attack' || event.action.type === 'lunge') this.#strikes.set(event.actorId, { started: event });
      }
      if (event.type === 'action-ended') {
        const strike = this.#strikes.get(event.actorId);
        if (strike && event.outcome === 'resolved') strike.resolved = event.tick;
        else this.#strikes.delete(event.actorId);
      }
      if (event.type === 'death' || event.type === 'interrupted') this.#strikes.delete(event.actorId);
      if (event.type === 'hit') {
        // The blow's weight is its share of the victim's health: a graze barely moves a body, a third of it staggers them.
        const power = Math.min(1, event.damage / Math.max(1, state.actors[event.targetId]!.maxHp) * 3), from = at(event.actorId);
        const same = (other: CombatEvent) => other.tick === event.tick;
        const turned = events.some(other => same(other) && other.type === 'blocked' && other.actorId === event.targetId && state.tactics?.actors[event.targetId]?.stance === 'braced');
        const broke = events.some(other => same(other) && other.type === 'action-ended' && other.actorId === event.targetId && other.outcome === 'interrupted' && other.reason === SPEAR_BREAK);
        renderer.impact(event.targetId, { from, sourceId: event.actorId, strength: broke ? 10 : 2 + 7 * power, hitStopMs: broke ? 110 : 45 + 60 * power,
          flash: turned ? 0 : power });
        if (broke) renderer.burst?.(event.targetId, 'break', { from, sourceId: event.actorId, power: 1 });
        else if (!turned) renderer.burst?.(event.targetId, 'hit', { from, sourceId: event.actorId, power });
      }
      // A braced shield turns the blow: sparks where bronze meets bronze, and the striker is thrown back off his line.
      if (event.type === 'blocked' && state.tactics?.actors[event.actorId]?.stance === 'braced') {
        renderer.burst?.(event.actorId, 'block', { from: at(event.targetId), sourceId: event.targetId, power: Math.min(1, event.prevented / 6) });
        renderer.impact(event.targetId, { from: at(event.actorId), sourceId: event.actorId, strength: 7, hitStopMs: 90, flash: 0 });
      }
      // A blow that meets only air carries the striker a little after it.
      if (event.type === 'action-ended' && event.outcome === 'blocked' && event.action.type === 'attack' && state.actors[event.action.targetId]) {
        const [x, y] = at(event.actorId), [tx, ty] = at(event.action.targetId);
        renderer.impact(event.actorId, { from: [2 * x - tx, 2 * y - ty], strength: 5, hitStopMs: 0, flash: 0 });
      }
      if (event.type === 'death') { renderer.burst?.(event.actorId, 'dust'); renderer.hitStop?.(120); }
      if (event.type === 'consumed') renderer.burst?.(event.actorId, 'treat');
      if (event.type === 'action-ended' && event.outcome === 'resolved'
        && (event.action.type === 'attack' || event.action.type === 'lunge')) {
        const gear = state.tactics?.continuity.persons[event.actorId]
          ? availableActions(state.tactics.continuity, event.actorId) : [];
        const kind = gear.includes('loose-arrow') ? 'arrow' : event.action.type === 'lunge' || gear.includes('thrust') ? 'thrust' : 'swing';
        // Only a resolved strike reaches the target. Misses keep the striker's
        // recoil above, without drawing a weapon through distance or cover.
        renderer.burst?.(event.actorId, kind, { from: at(event.action.targetId), sourceId: event.action.targetId });
      }
    }
    return events;
  }

  action(state: CombatState, id: string): ActionWindow | undefined {
    if (state.actors[id]?.status !== 'alive') return;
    const commitment = state.tactics?.actors[id]?.commitment;
    const strike = this.#strikes.get(id);
    const action = commitment?.action ?? strike?.started.action;
    if (!action || (action.type !== 'attack' && action.type !== 'lunge')) return;
    const target = state.actors[action.targetId];
    const toward = target ? { toward: toPixel(target.position), targetId: target.id } : {};
    if (commitment) return { phase: 'windup', progress: Math.max(0, Math.min(1,
      (state.tick - commitment.startedTick) / Math.max(1, commitment.resolvesTick - commitment.startedTick))), ...toward };
    if (strike?.resolved === undefined) return;
    if (state.tick === strike.resolved) return { phase: 'commit', progress: 1, ...toward };
    if (state.tick < strike.started.recoveryUntil) return { phase: 'recovery',
      progress: (state.tick - strike.resolved) / Math.max(1, strike.started.recoveryUntil - strike.resolved), ...toward };
    this.#strikes.delete(id);
  }

  activity(state: CombatState, id: string): PaintedActor['activity'] {
    return state.actors[id]?.status === 'alive' ? this.#activities.get(id) ?? 'idle' : 'idle';
  }
}
